/* High Rise — Catalog & Unified PDP (vanilla JS, no framework). */

(() => {
  'use strict';

  // Shopify port marker: data-page is set on .hr-landing, not body
  const isUnifiedPdp = document.querySelector('[data-hr-landing][data-page="unified-pdp"]')
                    || document.body.dataset.page === 'unified-pdp';

  // ------------------------------------------------------------
  // Shopify asset URL bridge.
  // Our prototype JS uses `./img/foo/bar.png` paths. In Shopify those
  // need to be served from /cdn/shop/t/<id>/assets/landing-foo-bar.png.
  // This patches Image.prototype + monkey-patches the few specific
  // getters our gallery uses, so existing code keeps working.
  // ------------------------------------------------------------
  const _hrAssetBase = (() => {
    const probe = document.querySelector('#hero-img')?.src
              || document.querySelector('img[src*="/cdn/shop/t/"]')?.src;
    if (!probe) return '';
    return probe.replace(/[^/]+$/, '');
  })();
  const _toLandingName = (p) =>
    p.replace(/^\.\/img\//, 'landing-').replace(/\//g, '-');
  const _hrAssetUrl = (p) => {
    if (!p) return p;
    if (p.startsWith('http') || p.startsWith('/cdn/')) return p;
    if (p.startsWith('./img/')) {
      const flat = _toLandingName(p);
      return _hrAssetBase + flat;
    }
    return p;
  };
  // Make available to functions defined later in this scope.
  window.__hrAssetUrl = _hrAssetUrl;

  // Auto-rewrite ./img/ paths — covers both direct img.src= assignments
  // AND innerHTML strings (the state machine builds <img src="./img/...">
  // strings in renderSkuScroller, renderFlavorScroller, renderGalleryThumbs).
  try {
    const _origSrc = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
    Object.defineProperty(HTMLImageElement.prototype, 'src', {
      configurable: true,
      get: function () { return _origSrc.get.call(this); },
      set: function (v) { _origSrc.set.call(this, _hrAssetUrl(v)); },
    });

    const _origIH = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    Object.defineProperty(Element.prototype, 'innerHTML', {
      configurable: true,
      get: function () { return _origIH.get.call(this); },
      set: function (v) {
        const rewritten = (typeof v === 'string')
          ? v.replace(/(["'])\.\/img\/([^"']+)\1/g,
              (_, q, p) => q + _hrAssetUrl('./img/' + p) + q)
          : v;
        _origIH.set.call(this, rewritten);
      },
    });
  } catch (e) { /* swallow — fallback is broken images, not crashes */ }

  // ============================================================
  // BRANCH A: Compliant catalog grid (index.html — unchanged)
  // ============================================================
  if (!isUnifiedPdp) {
    const PROD_BASES_COMPLIANT = {
      bottles:  'https://highrise-bottles.vercel.app/',
      seltzers: 'https://highrise-seltzers.vercel.app/',
    };
    const PROD_BASES_THC = {
      bottles:  'https://highrise-bottles.vercel.app/thc',
      seltzers: 'https://highrise-seltzers.vercel.app/thc',
    };
    const DEV_BASES_COMPLIANT = {
      bottles:  '/highrise/',
      seltzers: '/highrise-seltzers/',
    };
    const DEV_BASES_THC = {
      bottles:  '/highrise/thc.html',
      seltzers: '/highrise-seltzers/thc.html',
    };

    const isLocalDev = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(location.hostname)
                    || /\.local$/i.test(location.hostname);
    const isThcMode = document.body.dataset.mode === 'thc';
    const BASES = isLocalDev
      ? (isThcMode ? DEV_BASES_THC : DEV_BASES_COMPLIANT)
      : (isThcMode ? PROD_BASES_THC : PROD_BASES_COMPLIANT);

    document.querySelectorAll('.card[data-line]').forEach(a => {
      const line = a.dataset.line;
      const handle = a.dataset.handle;
      const base = BASES[line];
      if (base && handle) {
        const sep = base.includes('?') ? '&' : '?';
        a.href = `${base}${sep}p=${handle}`;
      }
    });

    const yearEl = document.getElementById('year');
    if (yearEl) yearEl.textContent = new Date().getFullYear();

    document.querySelectorAll('a[href^="#"]').forEach(a => {
      a.addEventListener('click', e => {
        const id = a.getAttribute('href').slice(1);
        if (!id) return;
        const target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
    return;
  }

  // ============================================================
  // BRANCH B: Unified PDP — Nowadays-style "choose your style".
  // Each top-row card is a SKU with the strength baked in
  // (e.g. "5mg 4-Pack", "10mg Bottle", "Sunset Sampler").
  // After picking a SKU, the row below shows flavors only when
  // applicable. Strength is implied by the SKU choice — we
  // never show a separate strength picker.
  // ============================================================

  // ---- Flavor leaves: each flavor lives once with its full content,
  //      keyed by handle. Strength lookups happen via PRODUCTS[h].strengths.
  const PRODUCTS = {
    // ===== Bottles (750mL) =====
    'bot-citrus': {
      name: 'Citrus', accent: '#89A84F',
      image: './img/bottles/citrus.png',
      tagline: 'Bright, balanced, lime-forward.',
      // Citrus pricing — verified from the Shopify variants JSON at
      // /products/thc-infused-citrus-750ml-bottle.json:
      //   5mg  → $55.99
      //   10mg → $65.99  (the rendered HTML hides this when both
      //   variants are sold out — only the JSON endpoint shows it.)
      strengths: { '5':{price:55.99}, '10':{price:65.99} },
      copy: `The High Rise Citrus Infusion is a citrus-forward, non-alcoholic pour designed for people who enjoy the ritual of a great drink, without the alcohol. Lime-forward, vibrant, refreshing — made with real fruit and organic cane sugar. Bright lift, smooth, creamy finish.`,
      pull: 'Each 750mL bottle = 15 servings of 50mL pours.',
      specs: [
        ['Volume',       '750mL · 15 servings (50mL)'],
        ['Profile',      'Bright · Lime · Creamy finish'],
        ['Calories',     '~20 per pour'],
        ['Ingredients',  'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',    'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.82, reviewCount: 96,
      reviews: [
        { title: 'My new dinner-party staple', body: 'Bright, balanced, easy to dose. We bought two for a dinner and the bottle made everyone feel like they were part of the ritual.', name: 'Allison R.' },
        { title: 'Lime-forward and clean', body: 'Tastes like a really well-made citrus tonic. The lift comes on slow and steady — exactly what the label promises.', name: 'Marcus T.' },
        { title: '15 servings = perfect for hosting', body: 'Pours like a real spirit. We got through one bottle over a slow Saturday — super easy to share without anyone overdoing it.', name: 'Priya N.' },
      ],
    },
    'bot-orange': {
      name: 'Orange', accent: '#F58B6F',
      image: './img/bottles/orange.png',
      tagline: 'Sun-kissed, juicy, soft creamy finish.',
      strengths: { '10':{price:65.99} },
      copy: `The High Rise Orange Infusion captures the warmth of a sun-kissed orange. Bright, expressive, layered with natural sweetness. Crafted with real fruit and organic cane sugar, it delivers vibrant citrus character with a soft, creamy finish that feels both nostalgic and modern.`,
      pull: 'Bold enough to anchor a build, clean enough to enjoy over ice.',
      specs: [
        ['Volume',       '750mL · 15 servings (50mL)'],
        ['Profile',      'Juicy · Sun-kissed · Creamy finish'],
        ['Calories',     '~20 per pour'],
        ['Ingredients',  'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',    'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.86, reviewCount: 74,
      reviews: [
        { title: 'Tastes like sunshine', body: "Honestly nostalgic — like a creamsicle but more grown up. The finish is what gets me.", name: 'Hannah B.' },
        { title: 'Best of the three', body: 'The orange is my favorite of the bunch. Smooth, creamy, just sweet enough.', name: 'Vince O.' },
        { title: 'Made me a believer', body: 'I was skeptical of bottles like this — this one converted me. Beautifully balanced.', name: 'Tasha M.' },
      ],
    },
    'bot-raspberry': {
      name: 'Raspberry', accent: '#DF4661',
      image: './img/bottles/raspberry.png',
      tagline: 'Lush, fruit-forward, vibrant yet refined.',
      strengths: { '10':{price:65.99} },
      copy: `The High Rise Raspberry Infusion is a fresh, fruit-forward pour. Lush raspberry notes meet gentle natural sweetness, vibrant yet refined. Smooth, creamy finish.`,
      pull: 'Bright, playful, and crafted for connection.',
      specs: [
        ['Volume',       '750mL · 15 servings (50mL)'],
        ['Profile',      'Lush · Fruit-forward · Smooth finish'],
        // Raspberry infusion is the only product at 25 cal — per
        // Stephanie's final-round note.
        ['Calories',     '~25 per pour'],
        ['Ingredients',  'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',    'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.79, reviewCount: 68,
      reviews: [
        { title: 'Fruit-forward and playful', body: 'The raspberry is bright without being syrupy. Mixes incredibly well with tonic.', name: 'Sasha L.' },
        { title: 'Great for a slow evening', body: 'Two pours into a long-stemmed glass and you are set. Beautiful, beautiful color.', name: 'Owen R.' },
        { title: 'My partner is obsessed', body: 'We finished a bottle in a week — that has never happened with anything similar.', name: 'Alyssa W.' },
      ],
    },

    // ===== 12oz × 4-pack flavors =====
    'can-blueberry': {
      name: 'Blueberry', accent: '#81B0D2',
      image: './img/seltzers/blueberry.png',
      tagline: 'Bright, jammy, slow-rolling.',
      strengths: {
        '3':  { price: 17.99, singlePrice: 5.49 },
        '5':  { price: 19.99, singlePrice: 5.99 },
        '10': { price: 24.99, singlePrice: 6.99 },
      },
      copy: `High Rise Blueberry is a fresh, fruit-forward, non-alcoholic seltzer. Real blueberry meets a soft natural sweetness — vibrant but refined. Smooth and crisp with a clean, easy lift.`,
      pull: 'Pour. Stay easy.',
      specs: [
        ['Pack',        '12oz × 4 cans'],
        ['Profile',     'Lush · Blueberry · Smooth'],
        ['Calories',    '~20 per can'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.81, reviewCount: 132,
      reviews: [
        { title: 'My new go-to', body: 'Stocking the fridge with these. The 5mg is exactly the lift I want without killing the next day.', name: 'Allison R.' },
        { title: 'Tastes like real blueberry', body: 'None of that fake-candy thing. Reminds me of a really good blueberry soda.', name: 'Marcus T.' },
        { title: 'Great with friends', body: 'Brought a 4-pack to a backyard hang. Everyone wanted to know where to get them.', name: 'Priya N.' },
      ],
    },
    'can-bloodorange': {
      name: 'Blood Orange', accent: '#FF6A39',
      image: './img/seltzers/bloodorange.png',
      tagline: 'Citrus-forward and just a little wild.',
      strengths: {
        '5':  { price: 19.99, singlePrice: 5.99 },
        '10': { price: 24.99, singlePrice: 6.99 },
      },
      copy: `Bright, juicy, just a touch wild. Real blood orange and organic cane sugar, balanced with a smooth, alcohol-free lift.`,
      pull: 'Citrus-forward. Slow-rolling.',
      specs: [
        ['Pack',        '12oz × 4 cans'],
        ['Profile',     'Bright · Citrus · Juicy'],
        ['Calories',    '~20 per can'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.83, reviewCount: 108,
      reviews: [
        { title: 'My favorite of the lineup', body: 'Blood Orange is just dialed. Bright, not too sweet, smooth landing.', name: 'Hannah B.' },
        { title: 'Goes with everything', body: 'Mixes great with sparkling water for a longer pour. Bring to every BBQ.', name: 'Vince O.' },
        { title: 'Better than expected', body: 'Real blood orange, not artificial.', name: 'Tasha M.' },
      ],
    },
    'can-pineapple': {
      name: 'Pineapple', accent: '#FED141',
      image: './img/seltzers/pineapple.png',
      tagline: 'Tropical, sunlit, made for long Saturdays.',
      strengths: {
        '3':  { price: 17.99, singlePrice: 5.49 },
        '5':  { price: 19.99, singlePrice: 5.99 },
        '10': { price: 24.99, singlePrice: 6.99 },
      },
      copy: `Tropical and sunlit. Real pineapple meets a soft cane sugar finish — bright, lively, easy to share.`,
      pull: 'Bright. Tropical. Pour-and-go.',
      specs: [
        ['Pack',        '12oz × 4 cans'],
        ['Profile',     'Tropical · Pineapple · Crisp'],
        ['Calories',    '~20 per can'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.79, reviewCount: 94,
      reviews: [
        { title: 'Sunshine in a can', body: 'Reminds me of a vacation drink without the next-day regret.', name: 'Owen R.' },
        { title: 'Perfect on the porch', body: 'Sat on the porch with a 4-pack and the sun. Very nice.', name: 'Sasha L.' },
        { title: 'Tastes like real pineapple', body: 'You can tell they used the real thing. Not syrupy.', name: 'Connor D.' },
      ],
    },
    'can-raspberry': {
      name: 'Raspberry', accent: '#DF4661',
      image: './img/seltzers/raspberry.png',
      tagline: 'Lush raspberry meets gentle sweetness.',
      strengths: {
        '10': { price: 24.99, singlePrice: 6.99 },
      },
      copy: `Lush raspberry meets gentle natural sweetness. Vibrant yet refined, smooth and creamy.`,
      pull: 'Lush. Bright. Made for connection.',
      specs: [
        ['Pack',        '12oz × 4 cans'],
        ['Profile',     'Lush · Raspberry · Smooth'],
        ['Calories',    '~20 per can'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.84, reviewCount: 76,
      reviews: [
        { title: 'Worth the upgrade', body: 'The 10mg has actual presence.', name: 'Andrew P.' },
        { title: 'Real raspberry', body: 'Real fruit comes through. Nothing artificial.', name: 'Maya G.' },
        { title: 'My weekend drink', body: 'Replaced my usual evening pour.', name: 'Chris W.' },
      ],
    },
    'can-grapefruit': {
      name: 'Grapefruit', accent: '#FF8DA1',
      image: './img/seltzers/grapefruit.png',
      tagline: 'Bitter-bright, crisp, the host\'s favorite.',
      strengths: {
        '5': { price: 19.99, singlePrice: 5.99 },
      },
      copy: `Bitter-bright grapefruit, dialed for the bar cart. Real fruit, smooth alcohol-free lift. Pours like a Paloma without the regret.`,
      pull: 'Bitter-bright. Crisp.',
      specs: [
        ['Pack',        '12oz × 4 cans'],
        ['Profile',     'Bitter-bright · Citrus · Crisp'],
        ['Calories',    '~20 per can'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.78, reviewCount: 64,
      reviews: [
        { title: 'Like a Paloma without the booze', body: 'I missed grapefruit cocktails. This is the perfect stand-in.', name: 'Nadia T.' },
        { title: 'Bright and clean', body: 'Crispest of the bunch. Not too sweet.', name: 'Diego H.' },
        { title: 'Bar-cart staple', body: 'Mix with sparkling water, salt rim — instant mocktail.', name: 'Kara B.' },
      ],
    },
    'can-mango': {
      name: 'Mango', accent: '#EF9600',
      image: './img/seltzers/mango.png',
      tagline: 'Ripe, sunlit, easy to love.',
      strengths: {
        '3': { price: 17.99, singlePrice: 5.49 },
        '5': { price: 19.99, singlePrice: 5.99 },
      },
      copy: `Ripe mango, soft sweetness, smooth finish. Sunlit and easy to love.`,
      pull: 'Ripe. Sunlit.',
      specs: [
        ['Pack',        '12oz × 4 cans'],
        ['Profile',     'Ripe · Mango · Soft sweetness'],
        ['Calories',    '~20 per can'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.77, reviewCount: 58,
      reviews: [
        { title: 'Like ripe mango', body: 'Soft and full, never artificial.', name: 'Sara P.' },
        { title: 'Easy 3mg', body: 'The 3mg is exactly the gentle lift I wanted as a lightweight.', name: 'Jonah M.' },
        { title: 'Perfect summer can', body: 'Drinks like a vacation.', name: 'Mira L.' },
      ],
    },

    // ===== Singular bundle products (one-and-only) =====
    'sunset-sampler': {
      name: 'Sunset Sampler', accent: '#FEC353',
      image: './img/sku/sunset-sampler.jpg',
      tagline: 'Two of each flavor at 5mg. Built to share.',
      strengths: { 'one':{price:37.99} },
      copy: `The Sunset Sampler is High Rise's 5mg variety pack — eight 12oz cans with two of each flavor. The easiest way to find your favorite, or to keep the bar stocked for everyone at the party.`,
      pull: 'Two of each flavor. Saves over single 4-packs.',
      specs: [
        ['Pack',        '12oz × 8 cans · variety'],
        ['Includes',    'Blueberry · Blood Orange · Pineapple · Mango'],
        ['Profile',     'Bright · Fruit-forward · Crisp'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.85, reviewCount: 142,
      reviews: [
        { title: 'Best way to try them all', body: 'Got the Sunset Sampler for a backyard hang. Everyone found a favorite by the end of the night.', name: 'Sasha K.' },
        { title: 'Two of each is just right', body: 'Means more than one person can try the same flavor. Smart pack.', name: 'Diego H.' },
        { title: 'Perfect host gift', body: 'Brought one to dinner. Hosts loved it.', name: 'Lina M.' },
      ],
    },
    'high-horizon': {
      name: 'High Horizon', accent: '#9E7498',
      image: './img/sku/high-horizon.png',
      tagline: 'Two of each flavor at 10mg. The full lift.',
      strengths: { 'one':{price:44.99} },
      copy: `High Horizon is the 10mg variety 8-pack — two cans each of Pineapple, Blood Orange, Raspberry, and Blueberry. Built for experienced sippers who want a real moment, with the variety to swap as the evening shifts.`,
      pull: 'A real lift, four flavors, one box.',
      specs: [
        ['Pack',        '12oz × 8 cans · variety'],
        ['Includes',    'Pineapple · Blood Orange · Raspberry · Blueberry'],
        ['Profile',     'Lush · Fruit-forward · Bold'],
        ['Ingredients', 'Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',   'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.88, reviewCount: 96,
      reviews: [
        { title: 'My weekend pack', body: 'A serious lift across four flavors.', name: 'Andrew P.' },
        { title: 'Real strength, real fruit', body: '10mg with this much flavor variety is rare.', name: 'Maya G.' },
        { title: 'Worth the price step', body: 'Saves vs. single 4-packs and the variety beats picking just one.', name: 'Chris W.' },
      ],
    },
    'blush-bundle': {
      name: 'Blush Bundle', accent: '#FF8DA1',
      image: './img/sku/blush.png',
      tagline: 'Raspberry 10mg + Grapefruit 5mg + glass tumbler.',
      // $56.98 per Highrise's live catalog (was $54.99 — corrected
      // during the full product audit).
      strengths: { 'one':{price:56.98} },
      copy: `The Blush Bundle pairs a 10mg Raspberry 4-pack with a 5mg Grapefruit 4-pack and a limited-edition glass tumbler. The pink-and-bright combo for slow nights at home.`,
      pull: 'Two 4-packs + a tumbler. Pour and stay.',
      specs: [
        ['Includes',   'Raspberry 10mg 4-pack + Grapefruit 5mg 4-pack + tumbler'],
        ['Pack',       '12oz × 8 cans + tumbler'],
        ['Profile',    'Lush · Bitter-bright · Bar-cart ready'],
        ['Ingredients','Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',  'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.88, reviewCount: 27,
      reviews: [
        { title: 'Beautiful gift', body: 'The tumbler tied the whole thing together.', name: 'Renée P.' },
        { title: 'Smart pairing', body: 'Raspberry and Grapefruit balance perfectly across an evening.', name: 'Kara B.' },
      ],
    },
    // 12-pack bundles: priced as 3× their underlying 4-pack price per
    // Highrise's live catalog. Big Three (10mg) = 3 × $24.99 = $74.97.
    // High Rise Flight (5mg) = 3 × $19.99 = $59.97.
    'big-three': {
      name: 'The Big Three', accent: '#DF4661',
      image: './img/sku/big-three.png',
      tagline: 'Three 4-packs at 10mg. Twelve cans, full lift.',
      strengths: { 'one':{price:74.97} },
      copy: `The Big Three is a 12-can bundle of three 4-packs at 10mg — Blood Orange, Blueberry, and Pineapple. Built for slow weekends and longer occasions.`,
      pull: 'Three flavors. Twelve cans. Full lift.',
      specs: [
        ['Pack',       '12oz × 12 cans · 3× 4-packs'],
        ['Includes',   'Blood Orange · Blueberry · Pineapple'],
        ['Profile',    'Lush · Fruit-forward · Bold'],
        ['Ingredients','Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',  'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.86, reviewCount: 64,
      reviews: [
        { title: 'My weekend stock-up', body: 'A dozen cans across three flavors keeps the fridge interesting all weekend.', name: 'Andrew P.' },
        { title: 'Worth the bundle savings', body: 'Cheaper than three 4-packs and you get the variety.', name: 'Maya G.' },
      ],
    },
    'flight': {
      name: 'High Rise Flight', accent: '#FEC353',
      image: './img/sku/flight.png',
      tagline: 'Three 4-packs at 5mg. Twelve cans, easy social lift.',
      strengths: { 'one':{price:59.97} },
      copy: `The High Rise Flight is the 5mg take on the 12-can bundle — three 4-packs of Blueberry, Pineapple, and Blood Orange. The sweet spot in social-pour quantity.`,
      pull: 'Three flavors. Twelve cans. Easy social lift.',
      specs: [
        ['Pack',       '12oz × 12 cans · 3× 4-packs'],
        ['Includes',   'Blueberry · Pineapple · Blood Orange'],
        ['Profile',    'Bright · Fruit-forward · Crisp'],
        ['Ingredients','Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',  'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.83, reviewCount: 27,
      reviews: [
        { title: 'Hosts go-to', body: 'Stock the cooler with one of these and you are set for the weekend.', name: 'Sasha K.' },
        { title: 'Smart bundle', body: 'Three flavors at the right strength for a long evening.', name: 'Diego H.' },
      ],
    },
    'weekender': {
      name: 'The Weekender', accent: '#EF9600',
      image: './img/sku/weekender.png',
      tagline: 'Three 5mg 4-packs. Built for the long Saturday.',
      // Three 4-packs at 5mg each: 3 × $19.99 = $59.97. Verified live.
      strengths: { 'one':{price:59.97} },
      copy: `The Weekender pairs three 4-packs of 5mg seltzers — Blueberry, Blood Orange, and Mango — for a 12-can bundle built for slow weekends and shared evenings.`,
      pull: 'Blueberry · Blood Orange · Mango. Three 5mg 4-packs.',
      specs: [
        ['Pack',       '12oz × 12 cans · 3× 4-packs'],
        ['Includes',   'Blueberry · Blood Orange · Mango'],
        ['Profile',    'Lush · Citrus · Tropical'],
        ['Ingredients','Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',  'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.84, reviewCount: 22,
      reviews: [
        { title: 'Real range', body: 'Three distinct flavors — covers every mood across a weekend.', name: 'Hannah B.' },
        { title: 'My new house pack', body: 'Mango is the surprise hit. Now reorder monthly.', name: 'Owen R.' },
      ],
    },
    'sunkissed': {
      name: 'Sun-Kissed Trio', accent: '#F58B6F',
      image: './img/sku/sunkissed.png',
      tagline: 'Mixed-strength trio. Two bold + one easy.',
      // $69.97 per Highrise's live catalog. Mixed strength: 2× 10mg
      // 4-packs + 1× 5mg 4-pack = 2($24.99) + $19.99 = $69.97.
      strengths: { 'one':{price:69.97} },
      copy: `The Sun-Kissed Trio mixes strengths for the best of both worlds — Blood Orange 10mg and Raspberry 10mg for bigger evenings, plus Blueberry 5mg for an easier pour. Three 4-packs, twelve cans.`,
      pull: 'Blood Orange 10mg · Raspberry 10mg · Blueberry 5mg.',
      specs: [
        ['Pack',       '12oz × 12 cans · 3× 4-packs'],
        ['Includes',   'Blood Orange 10mg · Raspberry 10mg · Blueberry 5mg'],
        ['Profile',    'Bright · Lush · Mixed strength'],
        ['Ingredients','Real fruit · Organic cane sugar · Hemp-derived botanicals'],
        ['Free from',  'Alcohol · Gluten · GMOs · Artificial flavors'],
      ],
      rating: 4.82, reviewCount: 8,
      reviews: [
        { title: 'Smart mix', body: 'Having both 10mg and 5mg in one bundle = perfect for hosting different people.', name: 'Diego H.' },
        { title: 'Pretty packaging too', body: 'Brought as a host gift, big hit.', name: 'Renée P.' },
      ],
    },
  };

  const BOTTLE_FLAVORS  = ['bot-citrus', 'bot-orange', 'bot-raspberry'];
  const SELTZER_FLAVORS = ['can-blueberry', 'can-bloodorange', 'can-pineapple',
                           'can-raspberry', 'can-grapefruit', 'can-mango'];

  // ---- SKUs (one per top-row card). `kind:flavored` = pick a flavor below;
  //      `kind:singular` = no flavor pick, this IS the product.
  //
  // ORDER is variety-forward, weighted by Highrise's actual sales data so
  // the top sellers + the strongest "we sell more than one type of drink"
  // signal hit the user first. Sequence:
  //   1. High Horizon (10mg 8-pack)   — #1 best seller
  //   2. 10mg 4-Pack                  — covers 4 of the top 10 flavors
  //   3. 5mg Bottle                   — bottle category, breaks up the can run
  //   4. 5mg 4-Pack                   — next-best individual format
  //   5. Sunset Sampler (5mg 8-pack)  — variety pack pair
  //   6. 10mg Bottle                  — completes the bottle category
  //   7. 5mg Single                   — single-can option
  //   8. Big Three (10mg 12-pack)     — top 12-pack
  //   9. 10mg Single                  — second single
  //  10. Blush Bundle (8-pack+tumbler)— themed pack
  //  11. High Rise Flight (5mg 12pk)  — second 12-pack
  //  12. 3mg Single                   — not in top 10, push to end
  //  13. 3mg 4-Pack                   — not in top 10, push to end
  // ---- Format tabs (Tier 1).
  // Each format has a key that matches the `format` field on each SKU.
  // Single Cans tab removed per Stephanie's final-round note — single SKUs
  // stay defined below (so deep links keep working) but aren't surfaced
  // in the catalog picker. Same goes for Blush Bundle (hidden:true).
  const FORMATS = [
    { key: 'bottle', label: 'Bottles' },
    { key: '8pack',  label: '8-Pack' },
    { key: '4pack',  label: '4-Pack' },
  ];

  const SKUS = {
    'pack-horizon': {
      kind: 'singular', name: 'High Horizon', sub: '10mg · 8-Pack',
      eyebrow: '10mg Variety · 12oz × 8-pack', meta: '8 cans · 2 of each flavor',
      thumbnail: './img/sku/high-horizon.png', product: 'high-horizon',
      format: '8pack', chipStrength: '10',
    },
    'can-4pack-10mg': {
      kind: 'flavored', name: '10mg 4-Pack', sub: '12oz × 4', strength: '10',
      eyebrow: '10mg Seltzer · 12oz × 4-pack', meta: '4 cans',
      thumbnail: './img/sku/4pack-10mg.png', flavors: SELTZER_FLAVORS,
      format: '4pack', chipStrength: '10',
    },
    'bottle-5mg': {
      kind: 'flavored', name: '5mg Bottle', sub: '750mL', strength: '5',
      eyebrow: '5mg Bottle · 750mL', meta: '15 servings · 50mL pours',
      // Tile thumbnail shows the 5mg-labelled bottle so the user can
      // tell strength tiers apart at a glance. The 10mg tile uses the
      // 10mg-labelled bottle below.
      thumbnail: './img/sku/bottle-5mg.png', flavors: BOTTLE_FLAVORS,
      format: 'bottle', chipStrength: '5',
    },
    'can-4pack-5mg': {
      kind: 'flavored', name: '5mg 4-Pack', sub: '12oz × 4', strength: '5',
      eyebrow: '5mg Seltzer · 12oz × 4-pack', meta: '4 cans',
      thumbnail: './img/sku/4pack-5mg.png', flavors: SELTZER_FLAVORS,
      format: '4pack', chipStrength: '5',
    },
    'pack-sunset': {
      kind: 'singular', name: 'Sunset Sampler', sub: '5mg · 8-Pack',
      eyebrow: '5mg Variety · 12oz × 8-pack', meta: '8 cans · 2 of each flavor',
      thumbnail: './img/sku/sunset-sampler.jpg', product: 'sunset-sampler',
      format: '8pack', chipStrength: '5',
    },
    'bottle-10mg': {
      kind: 'flavored', name: '10mg Bottle', sub: '750mL', strength: '10',
      eyebrow: '10mg Bottle · 750mL', meta: '15 servings · 50mL pours',
      thumbnail: './img/sku/bottle-10mg.png', flavors: BOTTLE_FLAVORS,
      format: 'bottle', chipStrength: '10',
    },
    // Single 12oz cans — pricing reads singlePrice from each flavor's
    // strengths object so a single can has its own per-can price.
    'can-single-5mg': {
      kind: 'flavored', name: '5mg Single', sub: 'Single 12oz', strength: '5',
      eyebrow: '5mg Seltzer · Single 12oz can', meta: '1 can',
      thumbnail: './img/sku/single-5mg.png', flavors: SELTZER_FLAVORS,
      pricingKey: 'singlePrice',
      format: 'single', chipStrength: '5',
    },
    'pack-bigthree': {
      kind: 'singular', name: 'The Big Three', sub: '10mg · 12-Pack',
      eyebrow: '10mg Bundle · 12oz × 12-pack', meta: '12 cans · 3× 4-packs',
      thumbnail: './img/sku/big-three.png', product: 'big-three',
      format: '4pack', chipStrength: '10',
    },
    'can-single-10mg': {
      kind: 'flavored', name: '10mg Single', sub: 'Single 12oz', strength: '10',
      eyebrow: '10mg Seltzer · Single 12oz can', meta: '1 can',
      thumbnail: './img/sku/single-10mg.png', flavors: SELTZER_FLAVORS,
      pricingKey: 'singlePrice',
      format: 'single', chipStrength: '10',
    },
    'pack-blush': {
      kind: 'singular', name: 'Blush Bundle', sub: '8-Pack + Tumbler',
      eyebrow: 'Blush Bundle · 8-pack + tumbler', meta: '8 cans + glass tumbler',
      thumbnail: './img/sku/blush.png', product: 'blush-bundle',
      // Hidden from the catalog picker per Stephanie's final-round note —
      // Blush is a mixed 5mg+10mg bundle so it doesn't fit cleanly under
      // either strength chip. SKU stays defined so deep links work.
      format: '8pack', chipStrength: 'any', hidden: true,
    },
    'pack-flight': {
      kind: 'singular', name: 'High Rise Flight', sub: '5mg · 12-Pack',
      eyebrow: '5mg Bundle · 12oz × 12-pack', meta: '12 cans · 3× 4-packs',
      thumbnail: './img/sku/flight.png', product: 'flight',
      format: '4pack', chipStrength: '5',
    },
    'pack-weekender': {
      kind: 'singular', name: 'The Weekender', sub: '5mg · 12-Pack',
      eyebrow: '5mg Bundle · 12oz × 12-pack', meta: '12 cans · 3× 4-packs (Blueberry · Blood Orange · Mango)',
      thumbnail: './img/sku/weekender.png', product: 'weekender',
      // 5mg 12-pack — same shelf as the High Rise Flight, different mix.
      // Flight has Pineapple; Weekender has Mango.
      format: '4pack', chipStrength: '5',
    },
    'pack-sunkissed': {
      kind: 'singular', name: 'Sun-Kissed Trio', sub: 'Mixed · 12-Pack',
      eyebrow: 'Mixed Bundle · 12oz × 12-pack', meta: '12 cans · Blood Orange 10mg · Raspberry 10mg · Blueberry 5mg',
      thumbnail: './img/sku/sunkissed.png', product: 'sunkissed',
      // Mixed-strength bundle (5mg + 10mg) — same approach as Blush
      // Bundle: hidden from the strength-chip picker but reachable
      // from the catalog's Bundles section + ?sku= deep link.
      format: '4pack', chipStrength: 'any', hidden: true,
    },
    'can-single-3mg': {
      kind: 'flavored', name: '3mg Single', sub: 'Single 12oz', strength: '3',
      eyebrow: '3mg Seltzer · Single 12oz can', meta: '1 can',
      thumbnail: './img/sku/single-3mg-v2.png', flavors: SELTZER_FLAVORS,
      pricingKey: 'singlePrice',
      format: 'single', chipStrength: '3',
    },
    'can-4pack-3mg': {
      kind: 'flavored', name: '3mg 4-Pack', sub: '12oz × 4', strength: '3',
      eyebrow: '3mg Seltzer · 12oz × 4-pack', meta: '4 cans',
      thumbnail: './img/sku/4pack-3mg-v2.png', flavors: SELTZER_FLAVORS,
      format: '4pack', chipStrength: '3',
    },
  };

  // Strength-chip ordering (low → high) so chips always read 3 · 5 · 10.
  const STRENGTH_ORDER = ['3', '5', '10'];
  // Derive which strength chips exist for a given format (sorted),
  // and whether the format has any SKUs without a strict strength
  // (Blush Bundle uses chipStrength:'any' which still belongs under both).
  const strengthsForFormat = (formatKey) => {
    const set = new Set();
    Object.values(SKUS).forEach((s) => {
      if (!s.hidden && s.format === formatKey &&
          s.chipStrength && s.chipStrength !== 'any') {
        set.add(s.chipStrength);
      }
    });
    return STRENGTH_ORDER.filter((k) => set.has(k));
  };
  const skusForFormatStrength = (formatKey, strengthKey) =>
    Object.entries(SKUS).filter(([, s]) =>
      !s.hidden &&
      s.format === formatKey &&
      (s.chipStrength === strengthKey || s.chipStrength === 'any')
    );
  // Count SKUs hiding behind every OTHER strength chip in a format.
  // Powers the "+N more at other strengths" affordance so users on
  // a single chip can see that switching chips would reveal more
  // options — fixes the discoverability gap without rendering every
  // SKU side-by-side.
  const skuCountAtOtherStrengths = (formatKey, currentKey) => {
    let n = 0;
    Object.values(SKUS).forEach((s) => {
      if (s.hidden || s.format !== formatKey) return;
      if (!s.chipStrength || s.chipStrength === 'any') return;
      if (s.chipStrength !== currentKey) n++;
    });
    return n;
  };

  const fmt = (n) => '$' + Number(n).toFixed(2);
  const setText = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  const setHTML = (id, val) => { const el = document.getElementById(id); if (el) el.innerHTML = val; };

  // ---------- DOM refs ----------
  const heroImg        = document.getElementById('hero-img');
  const heroFormat     = document.getElementById('hero-format');
  const heroName       = document.getElementById('upd-name');
  const heroTagline    = document.getElementById('hero-tagline');
  const heroPrice      = document.getElementById('hero-price');
  const heroMeta       = document.getElementById('hero-meta');
  const formatTabs     = document.getElementById('format-tabs');
  const strengthChips  = document.getElementById('strength-chips');
  const skuScroller    = document.getElementById('format-scroller');
  const flavorScroller = document.getElementById('flavor-scroller');
  const flavorPicker   = document.getElementById('flavor-picker');
  const atcBtn         = document.getElementById('atc-btn');
  const yearEl         = document.getElementById('year');

  // ---------- State ----------
  // Default to the #1 best seller (High Horizon) so the user lands on
  // the top variety pack first — immediately signals "lots of flavors".
  let currentSku      = 'pack-horizon';
  let currentFlavor   = 'high-horizon';
  // Derived from currentSku — kept in sync via syncFormatStateFromSku().
  let currentFormat   = SKUS[currentSku].format;
  let currentStrength = SKUS[currentSku].chipStrength === 'any'
                        ? strengthsForFormat(currentFormat)[0]
                        : SKUS[currentSku].chipStrength;

  // Stay Stocked subscription discount — matches the bottles/seltzers PDPs
  // so subscription pricing is consistent across every product page.
  const SUB_DISCOUNT = 0.15;

  // ---------- Smoothness helpers ----------
  // Toggle .is-active + aria-selected on a button group without re-rendering.
  // Keeping the DOM stable lets CSS transitions on border/background actually
  // play out — innerHTML rebuilds destroy the elements mid-transition and
  // make every click feel jumpy.
  const setActiveButton = (parent, attr, val) => {
    if (!parent) return;
    parent.querySelectorAll(`[${attr}]`).forEach((b) => {
      const me = b.getAttribute(attr) === val;
      b.classList.toggle('is-active', me);
      b.setAttribute('aria-selected', String(me));
    });
  };

  // Cache the last filter signature so we only rebuild when the underlying
  // SKU/chip set actually changes. Clicks within the same set just toggle
  // classes — smooth, no flicker.
  let lastChipFormat = '';
  let lastSkuFilter  = '';

  // ---------- Tier 1: format tabs ----------
  // Format tabs are static, so build once. A single absolutely-positioned
  // indicator slides between tabs (CSS transition on transform + width)
  // instead of each tab toggling its own background — that's why the
  // active highlight now glides instead of snapping.
  const renderFormatTabs = () => {
    if (!formatTabs) return;
    if (!formatTabs.children.length) {
      formatTabs.classList.add('is-priming');
      formatTabs.innerHTML =
        '<span class="format-tabs__indicator" aria-hidden="true"></span>' +
        FORMATS.map(({ key, label }) => `
          <button class="format-tab"
                  data-format="${key}" type="button"
                  role="tab" aria-selected="false">
            <span class="format-tab__label">${label}</span>
          </button>`).join('');
      formatTabs.querySelectorAll('.format-tab').forEach((b) => {
        b.addEventListener('click', () => selectFormat(b.dataset.format));
      });
      // Re-position the indicator when the container resizes (e.g. mobile
      // orientation change) so it stays glued to the active tab.
      window.addEventListener('resize', positionFormatIndicator);
    }
    setActiveButton(formatTabs, 'data-format', currentFormat);
    positionFormatIndicator();
  };

  // Compute and write the indicator's offset + width as CSS vars. Called
  // after every format change, on init, and on window resize.
  const positionFormatIndicator = () => {
    if (!formatTabs) return;
    const active = formatTabs.querySelector('.format-tab.is-active');
    if (!active) return;
    formatTabs.style.setProperty('--tab-x', `${active.offsetLeft}px`);
    formatTabs.style.setProperty('--tab-w', `${active.offsetWidth}px`);
    // Drop the priming flag after the first measurement so future moves
    // animate (it suppresses the very first jump-from-zero on init).
    if (formatTabs.classList.contains('is-priming')) {
      requestAnimationFrame(() => formatTabs.classList.remove('is-priming'));
    }
  };

  // ---------- Tier 2: strength chips ----------
  // Rebuild only when the format changes (chip set differs per format).
  // Otherwise just flip which chip is active.
  //
  // Discoverability nudge: a small "+N more →" affordance is appended
  // to the right of the chips, showing how many SKUs live behind the
  // OTHER strength chips in this format. Clicking it advances to the
  // next chip — so a user on 10mg sees "+3 more →", taps it, and lands
  // on 5mg without having to know which chip to click. The hint
  // auto-hides when there's nothing more to discover (single-chip
  // formats or the count is 0).
  const renderStrengthChips = () => {
    if (!strengthChips) return;
    const chips = strengthsForFormat(currentFormat);
    if (lastChipFormat !== currentFormat) {
      strengthChips.innerHTML = chips.map((k) => `
        <button class="strength-chip"
                data-strength="${k}" type="button"
                role="tab" aria-selected="false">${k}mg</button>`).join('') +
        `<button class="strength-chips__hint" type="button"
                 aria-label="See more options at other strengths">
           <svg class="strength-chips__hint-arrow" width="14" height="14"
                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
             <path d="m15 18-6-6 6-6"/>
           </svg>
           <span class="strength-chips__hint-text"></span>
         </button>`;
      strengthChips.querySelectorAll('.strength-chip').forEach((b) => {
        b.addEventListener('click', () => selectStrength(b.dataset.strength));
      });
      const hintBtn = strengthChips.querySelector('.strength-chips__hint');
      if (hintBtn) {
        hintBtn.addEventListener('click', () => {
          // Cycle to the next strength chip in display order.
          const all = strengthsForFormat(currentFormat);
          if (!all.length) return;
          const i = all.indexOf(currentStrength);
          const next = all[(i + 1) % all.length];
          if (next && next !== currentStrength) selectStrength(next);
        });
      }
      lastChipFormat = currentFormat;
    }
    setActiveButton(strengthChips, 'data-strength', currentStrength);
    // Show / hide the discoverability hint. We keep the count math
    // only to know IF there's something more to discover (so the
    // hint hides on single-strength formats) — but the label itself
    // reads "See more" with no number, since the count was making
    // people pause to interpret what it meant.
    const hintBtn  = strengthChips.querySelector('.strength-chips__hint');
    const hintTxt  = strengthChips.querySelector('.strength-chips__hint-text');
    const moreN    = skuCountAtOtherStrengths(currentFormat, currentStrength);
    if (hintBtn && hintTxt) {
      hintTxt.textContent = 'See more';
      hintBtn.hidden = moreN <= 0 || chips.length <= 1;
    }
    // Hide the row entirely if a format has only one strength.
    strengthChips.hidden = chips.length <= 1;
  };

  // ---------- Tier 3: SKU result grid ----------
  // Rebuild only when (format, strength) changes. Clicking a different SKU
  // within the same filter just toggles which tile is active — no DOM
  // rebuild, so the existing border/background transitions play smoothly.
  const renderSkuScroller = () => {
    if (!skuScroller) return;
    const filterKey = `${currentFormat}|${currentStrength}`;
    if (lastSkuFilter !== filterKey) {
      const tiles = skusForFormatStrength(currentFormat, currentStrength);
      skuScroller.innerHTML = tiles.map(([key, s]) => `
        <button class="scroller__card"
                data-sku="${key}" type="button"
                role="tab" aria-selected="false">
          <span class="scroller__shot">
            <img src="${s.thumbnail}" alt="" loading="lazy" decoding="async" />
          </span>
          <span class="scroller__name">${s.name}</span>
          <span class="scroller__sub">${s.sub}</span>
        </button>`).join('');
      skuScroller.querySelectorAll('.scroller__card').forEach((b) => {
        b.addEventListener('click', () => selectSku(b.dataset.sku));
      });
      lastSkuFilter = filterKey;
    }
    setActiveButton(skuScroller, 'data-sku', currentSku);
  };

  // ---------- Hero image — smooth crossfade swap ----------
  // Avoids the hard cut + white-flash you get from a bare `img.src = …`.
  // Fades out, swaps src once the new image is ready, fades back in.
  // First call after page load is direct (no fade) so the initial product
  // selection doesn't flicker before the user has done anything.
  let heroSwapInit = false;
  const swapHero = (src, alt) => {
    if (!heroImg || !src) return;
    // Skip the dance if it's already the same image.
    const cur = heroImg.getAttribute('src') || '';
    if (cur === src) {
      if (alt && heroImg.alt !== alt) heroImg.alt = alt;
      heroSwapInit = true;
      return;
    }
    // First swap after load: just set it, no fade (prevents on-load flash).
    if (!heroSwapInit) {
      heroImg.src = src;
      if (alt) heroImg.alt = alt;
      heroSwapInit = true;
      return;
    }
    const FADE_MS = 180;
    heroImg.classList.add('is-swapping');
    setTimeout(() => {
      const reveal = () => heroImg.classList.remove('is-swapping');
      const onLoad = () => {
        // Double rAF so opacity transition restarts from 0 → 1.
        requestAnimationFrame(() => requestAnimationFrame(reveal));
      };
      // Preload via a temp Image so we don't swap until pixels are ready.
      const tmp = new Image();
      tmp.onload = () => {
        heroImg.src = src;
        if (alt) heroImg.alt = alt;
        onLoad();
      };
      tmp.onerror = onLoad;  // still reveal so we don't get stuck at 0 opacity
      tmp.src = src;
      // Safety net: never stay invisible longer than ~1s.
      setTimeout(reveal, 900);
    }, FADE_MS);
  };

  // ---------- Tab / chip selection ----------
  // Picking a format auto-selects a strength chip and the first SKU,
  // so the user always lands on a valid product after one click.
  // Strength stays sticky across format changes when possible — e.g. a
  // user looking at 10mg 4-packs who switches to Bottles stays on
  // 10mg bottles instead of getting bounced to 5mg.
  const selectFormat = (formatKey) => {
    if (!FORMATS.some((f) => f.key === formatKey)) return;
    currentFormat = formatKey;
    const chips = strengthsForFormat(formatKey);
    if (!chips.includes(currentStrength)) currentStrength = chips[0] || '';
    const firstSku = skusForFormatStrength(formatKey, currentStrength)[0];
    if (firstSku) selectSku(firstSku[0]);  // selectSku handles all re-rendering.
  };

  // Picking a strength filters the visible SKUs and selects the first.
  // selectSku handles all re-rendering, so no need to call it here.
  const selectStrength = (strengthKey) => {
    currentStrength = strengthKey;
    const firstSku = skusForFormatStrength(currentFormat, strengthKey)[0];
    if (firstSku) selectSku(firstSku[0]);
  };

  // Picking a SKU updates the buybox / hero / flavor scroller.
  // Format and strength stay in sync with whichever tile was clicked.
  const selectSku = (skuKey) => {
    if (!SKUS[skuKey]) return;
    currentSku = skuKey;
    const sku = SKUS[skuKey];
    // Keep format/strength state aligned with the chosen SKU. If the
    // SKU uses chipStrength:'any' (Blush) we keep whatever strength
    // chip the user clicked from, since it's valid for both.
    if (sku.format !== currentFormat) currentFormat = sku.format;
    if (sku.chipStrength && sku.chipStrength !== 'any' &&
        sku.chipStrength !== currentStrength) {
      currentStrength = sku.chipStrength;
    }
    renderFormatTabs();
    renderStrengthChips();
    renderSkuScroller();
    // Pick the first available flavor (or the singular product)
    if (sku.kind === 'singular') {
      selectFlavor(sku.product);
    } else {
      const available = sku.flavors.filter((h) => PRODUCTS[h]?.strengths?.[sku.strength]);
      if (available.length) selectFlavor(available[0]);
    }
  };

  // Compute the right thumbnail image for a flavor under a given SKU.
  // Singles → matching strength's `can.png`; 4-Packs → matching strength's
  // `4pack.png`. Bottles use their bottle shot. Every flavor that ships
  // at 3mg (Blueberry, Pineapple, Mango) has its own 3mg-labelled can
  // image pulled from Highrise's CDN, so we never fall back to 5mg art
  // when the SKU strength is 3mg.
  const flavorImageForSku = (flavorHandle, sku) => {
    const p = PRODUCTS[flavorHandle];
    if (!p) return '';
    if (sku.kind === 'singular') return p.image;
    if (flavorHandle.startsWith('bot-')) {
      const f = flavorHandle.replace('bot-', '');
      // Citrus ships at both 5mg and 10mg — the bottle label changes
      // between the two so we surface the strength-specific shot. The
      // other bottle flavors (Orange, Raspberry) only ship at 10mg per
      // highrisebev.com, so they fall through to the default front.png.
      if (f === 'citrus' && (sku.chipStrength === '5' || sku.strength === '5')) {
        return `./img/bottles/citrus/front-5mg.png`;
      }
      if (f === 'citrus' && (sku.chipStrength === '10' || sku.strength === '10')) {
        return `./img/bottles/citrus/front-10mg.png`;
      }
      return `./img/bottles/${f}/front.png`;
    }
    const handle = flavorHandle.replace('can-', '');
    const variant = sku.name.includes('4-Pack') ? '4pack' : 'can';
    return `./img/seltzers/${handle}/${sku.strength}mg/${variant}.png`;
  };

  // ---------- Hero gallery ----------
  // Returns up to 4 images for the thumbnail strip. Mirrors the bottles
  // and seltzers PDPs so the catalog page reads as part of the same site.
  // `kind: 'product'` thumbs get `object-fit: contain` (transparent PNGs);
  // `kind: 'scene'` thumbs get `cover` (lifestyle photos).
  const galleryImagesFor = (sku, flavorHandle) => {
    const p = PRODUCTS[flavorHandle];
    if (!p) return [];
    // Bottles — use the new multi-angle folder. Citrus has per-strength
    // front art (5mg vs 10mg) since both ship on Highrise; other bottle
    // flavors (Orange, Raspberry) only ship at 10mg.
    if (flavorHandle.startsWith('bot-')) {
      const f = flavorHandle.replace('bot-', '');
      if (f === 'citrus') {
        const sk = sku.chipStrength || sku.strength;
        const frontSrc = sk === '5'
          ? './img/bottles/citrus/front-5mg.png'
          : sk === '10'
            ? './img/bottles/citrus/front-10mg.png'
            : './img/bottles/citrus/front.png';
        return [
          { slot: 'front',     kind: 'product', src: frontSrc },
          { slot: 'lifestyle', kind: 'scene',   src: './img/bottles/citrus/lifestyle.png' },
          { slot: 'pair',      kind: 'product', src: './img/bottles/citrus/two-bottles.png' },
          { slot: 'detail',    kind: 'scene',   src: './img/bottles/citrus/back.png' },
        ];
      }
      if (f === 'orange') return [
        { slot: 'front',     kind: 'product', src: './img/bottles/orange/front.png' },
        { slot: 'lifestyle', kind: 'scene',   src: './img/bottles/orange/lifestyle.png' },
        { slot: 'scene-1',   kind: 'scene',   src: './img/bottles/orange/horizontal.jpg' },
        { slot: 'scene-2',   kind: 'scene',   src: './img/bottles/orange/vertical.jpg' },
      ];
      if (f === 'raspberry') return [
        { slot: 'front',     kind: 'product', src: './img/bottles/raspberry/front.png' },
        { slot: 'lifestyle', kind: 'scene',   src: './img/bottles/raspberry/lifestyle.png' },
        { slot: 'scene-1',   kind: 'scene',   src: './img/bottles/raspberry/horizontal.jpg' },
        { slot: 'scene-2',   kind: 'scene',   src: './img/bottles/raspberry/blackout.jpg' },
      ];
    }
    // Flavored seltzer SKUs — per-strength folder has 4 angles each.
    if (sku.kind === 'flavored' && !flavorHandle.startsWith('bot-')) {
      const handle = flavorHandle.replace('can-', '');
      const strength = sku.strength;
      const dir = `./img/seltzers/${handle}/${strength}mg`;
      return [
        { slot: 'can',       kind: 'product', src: `${dir}/can.png` },
        { slot: '4pack',     kind: 'product', src: `${dir}/4pack.png` },
        { slot: 'lifestyle', kind: 'scene',   src: `${dir}/lifestyle.png` },
        { slot: 'pour',      kind: 'scene',   src: `${dir}/pour.png` },
      ];
    }
    // Variety packs / bundles — single pack shot + 3 brand lifestyle
    // scenes that include High Rise product, so the thumb strip stays
    // full and on-brand without inventing new product art.
    return [
      { slot: 'front',   kind: 'product', src: p.image },
      { slot: 'scene-1', kind: 'scene',   src: './img/lifestyle/final-001.jpg' },
      { slot: 'scene-2', kind: 'scene',   src: './img/lifestyle/final-020.jpg' },
      { slot: 'scene-3', kind: 'scene',   src: './img/lifestyle/final-040.jpg' },
    ];
  };

  // Render the thumb strip + wire clicks to swap the hero. Active thumb
  // is whichever currently matches the hero src.
  const thumbsEl = document.getElementById('upd-thumbs');
  let activeHeroSrc = '';
  const renderGalleryThumbs = (images) => {
    if (!thumbsEl) return;
    thumbsEl.innerHTML = images.map((im, i) => `
      <button class="upd-thumb"
              data-slot="${im.slot}" data-kind="${im.kind}"
              data-src="${im.src}"
              type="button" role="tab"
              aria-label="View ${im.slot.replace(/-/g, ' ')}"
              aria-selected="${i === 0 ? 'true' : 'false'}">
        <img src="${im.src}" alt="" loading="lazy" decoding="async" />
      </button>`).join('');
    thumbsEl.querySelectorAll('.upd-thumb').forEach((b) => {
      b.addEventListener('click', () => {
        const src = b.dataset.src;
        if (!src) return;
        swapHero(src, heroImg?.alt || '');
        activeHeroSrc = src;
        thumbsEl.querySelectorAll('.upd-thumb').forEach((x) => {
          const me = x === b;
          x.classList.toggle('is-active', me);
          x.setAttribute('aria-selected', String(me));
        });
      });
    });
    // Mark the first thumb (the main product shot) as active by default.
    const first = thumbsEl.querySelector('.upd-thumb');
    if (first) first.classList.add('is-active');
    activeHeroSrc = images[0]?.src || '';
  };

  // ---------- Flavor scroller (rendered dynamically) ----------
  // Rebuild only when the flavor set actually changes (different SKU). A
  // click within the same SKU just toggles which flavor card is active —
  // smooth border-color + tint transitions take over from there.
  let lastFlavorFilter = '';
  const renderFlavorScroller = () => {
    if (!flavorScroller) return;
    const sku = SKUS[currentSku];
    if (sku.kind === 'singular') {
      // No flavor pick for singular products — hide the row entirely.
      if (flavorPicker) flavorPicker.hidden = true;
      lastFlavorFilter = '';
      flavorScroller.innerHTML = '';
      return;
    }
    if (flavorPicker) flavorPicker.hidden = false;
    const available = sku.flavors.filter(h => PRODUCTS[h]?.strengths?.[sku.strength]);
    const filterKey = `${currentSku}|${sku.strength || ''}|${available.join(',')}`;
    if (lastFlavorFilter !== filterKey) {
      flavorScroller.innerHTML = available.map(handle => {
        const p = PRODUCTS[handle];
        const img = flavorImageForSku(handle, sku);
        return `
          <button class="scroller__card"
                  data-flavor="${handle}" type="button"
                  role="tab" aria-selected="false"
                  style="--accent: ${p.accent};">
            <span class="scroller__shot">
              <img src="${img}" alt="" loading="lazy" decoding="async" />
            </span>
            <span class="scroller__name">${p.name}</span>
          </button>`;
      }).join('');
      flavorScroller.querySelectorAll('.scroller__card').forEach(b => {
        b.addEventListener('click', () => selectFlavor(b.dataset.flavor));
      });
      lastFlavorFilter = filterKey;
    }
    setActiveButton(flavorScroller, 'data-flavor', currentFlavor);
  };

  // ---------- Per-product detail render ----------
  const renderInfoBlock = () => {
    const p = PRODUCTS[currentFlavor];
    setText('info-copy', p.copy);
    setText('info-pull', p.pull);
    setHTML('info-specs', p.specs.map(([k, v]) =>
      `<li><span>${k}</span><strong>${v}</strong></li>`).join(''));
  };

  const renderReviews = () => {
    const p = PRODUCTS[currentFlavor];
    setText('rev-rating', p.rating.toFixed(2));
    setText('rev-count', p.reviewCount);
    // Compact rating line under the title.
    setText('upd-rating-num', p.rating.toFixed(2));
    setText('upd-rating-count', `${p.reviewCount} reviews`);
    setHTML('reviews-list', p.reviews.map(r => `
      <li class="rv-card">
        <div class="rv-card__head">
          <div class="rv-card__stars" aria-hidden="true">★★★★★</div>
          <strong class="rv-card__title">${r.title}</strong>
        </div>
        <blockquote>“${r.body}”</blockquote>
        <footer><strong>${r.name}</strong> · <span>Verified Buyer</span></footer>
      </li>`).join(''));
  };

  // ---------- Flavor selection ----------
  const selectFlavor = (handle) => {
    if (!PRODUCTS[handle]) return;
    currentFlavor = handle;
    const p   = PRODUCTS[handle];
    const sku = SKUS[currentSku];
    const strengthKey = sku.kind === 'singular' ? 'one' : sku.strength;
    // Pricing key — defaults to 'price' (4-pack/bottle). Single-can SKUs
    // override with `pricingKey: 'singlePrice'` so the same flavor entry
    // serves both the single and the multi-pack at different price points.
    const priceKey = sku.pricingKey || 'price';
    const basePrice = p.strengths[strengthKey]?.[priceKey]
                   ?? p.strengths[strengthKey]?.price
                   ?? Object.values(p.strengths)[0].price;
    // Apply the Stay Stocked discount if the user has subscription selected.
    const isSub = isSubscription();
    const price = isSub ? basePrice * (1 - SUB_DISCOUNT) : basePrice;
    // Refresh the purchase-card prices (one-time + subscribe).
    updatePurchaseCardPrices(basePrice);

    document.body.setAttribute('data-flavor', handle);
    document.body.style.setProperty('--accent', p.accent);

    // Hero — match the SKU type so a 10mg 4-Pack of Blueberry shows the
    // 4-pack BOX shot, not a single can shot. swapHero() fades the image
    // out, preloads the new one, then fades back in (no hard cut / flash).
    const heroSrc = flavorImageForSku(handle, sku);
    swapHero(heroSrc, `High Rise ${p.name}`);
    // Rebuild the gallery thumb strip below the hero for the current
    // SKU + flavor. First thumb is the main hero shot (auto-active).
    renderGalleryThumbs(galleryImagesFor(sku, handle));
    if (heroFormat)  heroFormat.textContent  = sku.eyebrow;
    if (heroName)    heroName.textContent    = sku.kind === 'singular' ? p.name : p.name;
    if (heroTagline) heroTagline.textContent = p.tagline;
    if (heroPrice)   heroPrice.textContent   = fmt(price);
    if (heroMeta) {
      const cadence = isSub ? `Stay Stocked · every ${freqSelect?.value || 4} wks` : sku.meta;
      heroMeta.textContent = cadence;
    }

    renderFlavorScroller();
    renderInfoBlock();
    renderReviews();
  };

  // ---------- Purchase type (One-Time vs Stay Stocked) ----------
  const purchaseCards  = [...document.querySelectorAll('.purchase-card')];
  const purchaseInputs = [...document.querySelectorAll('input[name="purchase"]')];
  const onetimePriceEl = document.querySelector('[data-onetime-price]');
  const subPriceEl     = document.querySelector('[data-sub-price]');
  const strikePriceEl  = document.querySelector('[data-strike-price]');
  const freqSelect     = document.getElementById('freq-select');

  const isSubscription = () =>
    purchaseInputs.find(i => i.checked)?.value === 'subscribe';

  const updatePurchaseCardPrices = (basePrice) => {
    if (onetimePriceEl) onetimePriceEl.textContent = fmt(basePrice);
    if (subPriceEl)     subPriceEl.textContent     = fmt(basePrice * (1 - SUB_DISCOUNT));
    if (strikePriceEl)  strikePriceEl.textContent  = fmt(basePrice);
  };

  const refreshActiveCard = () => {
    purchaseCards.forEach(card => {
      const input = card.querySelector('input');
      card.setAttribute('data-active', String(!!input?.checked));
    });
  };

  // Clicking anywhere on a purchase card flips its radio + refreshes the
  // main price/ATC by re-running flavor selection (cheapest way to keep
  // base-price logic in one place).
  purchaseCards.forEach(card => {
    card.addEventListener('click', () => {
      const input = card.querySelector('input');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });
  purchaseInputs.forEach(i => i.addEventListener('change', () => {
    refreshActiveCard();
    // Re-render hero price using the current flavor — keeps all price
    // logic centralized in selectFlavor.
    selectFlavor(currentFlavor);
  }));
  freqSelect?.addEventListener('change', () => selectFlavor(currentFlavor));
  refreshActiveCard();

  // ---------- ATC (Shopify cart integration) ----------
  // Resolves the active (sku, flavor, strength) into a Shopify variant_id
  // via window.HR_VARIETY_DATA + window.HR_PRODUCT_LOOKUP (rendered live
  // from Liquid in page.variety.liquid) and POSTs to /cart/add.js.
  if (atcBtn) {
    atcBtn.addEventListener('click', async () => {
      if (atcBtn.disabled) return;
      const skuData = window.HR_VARIETY_DATA?.[currentSku];
      if (!skuData) {
        console.warn('[variety ATC] no HR_VARIETY_DATA for', currentSku);
        return;
      }
      let handle = skuData.handle;
      if (!handle && skuData.perFlavorHandle) {
        handle = skuData.perFlavorHandle[currentFlavor];
      }
      const productInfo = handle ? window.HR_PRODUCT_LOOKUP?.[handle] : null;
      if (!productInfo) {
        console.warn('[variety ATC] no product lookup for handle', handle);
        return;
      }

      const isSub = document.querySelector('input[name="purchase"]:checked')?.value === 'subscribe';
      let sellingPlanId = null;
      if (isSub) {
        const freq = document.getElementById('freq-select')?.value;
        // Word-boundary regex so "2 week" doesn't match "12 weeks" later.
        // Today plans are "Ships every 2|4|8 weeks" so includes() works,
        // but this is one Shopify admin click from being wrong.
        const freqRe = new RegExp(`\\b${freq}\\s*weeks?\\b`, 'i');
        const plan = (productInfo.sellingPlans || []).find((sp) => freqRe.test(sp.name))
                  || productInfo.sellingPlans?.[0];
        if (plan) sellingPlanId = plan.id;
      }

      const lab = atcBtn.querySelector('.upd-atc__label');
      const origText = lab?.textContent || 'Add to Cart';
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');

      try {
        const root = document.querySelector('[data-hr-landing]');
        if (root?.__hr?.addToCart) {
          await root.__hr.addToCart({
            variantId: productInfo.variantId,
            quantity: 1,
            sellingPlanId,
          });
          root.__hr.showToast?.('Added to cart', 'success');
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            setTimeout(() => drawer.open(atcBtn), 280);
          }
        } else {
          // Fallback if landing.js helper isn't loaded yet
          const body = { items: [{ id: productInfo.variantId, quantity: 1, ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}) }] };
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(body),
          });
          if (!r.ok) throw new Error('Add failed');
          window.location = '/cart';
        }
        if (lab) lab.textContent = 'Added ✓';
      } catch (err) {
        if (lab) lab.textContent = 'Try again';
        console.error('[variety ATC]', err);
      } finally {
        setTimeout(() => {
          if (lab) lab.textContent = origText;
          atcBtn.disabled = false;
          atcBtn.removeAttribute('aria-busy');
        }, 1800);
      }
    });
  }

  // ---------- FAQ tabs ----------
  const faqTabs   = [...document.querySelectorAll('.faq-tab')];
  const faqPanels = [...document.querySelectorAll('.faq-panel')];
  const setFaqStrength = (sk) => {
    if (!faqTabs.length) return;
    faqTabs.forEach(t => {
      const me = t.dataset.strength === String(sk);
      t.classList.toggle('is-active', me);
      t.setAttribute('aria-selected', String(me));
    });
    faqPanels.forEach(p => { p.hidden = p.dataset.strength !== String(sk); });
  };
  faqTabs.forEach(t => t.addEventListener('click', () => setFaqStrength(t.dataset.strength)));

  // ---------- Scroll hint on the SKU carousel (mobile only) ----------
  // Hint stays visible while the user can still scroll further right.
  // Disappears once they've reached the end (last card fully in view),
  // or if the row already fits without needing horizontal scroll at all.
  const setupScrollHint = () => {
    const scroller = document.getElementById('format-scroller');
    const wrap = scroller?.parentElement;
    const hint = document.getElementById('scroll-hint');
    if (!scroller || !wrap || !hint) return;
    const update = () => {
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const isScrollable = maxScroll > 4;
      const atEnd = scroller.scrollLeft >= maxScroll - 4;
      hint.classList.toggle('is-hidden', !isScrollable || atEnd);
    };
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    requestAnimationFrame(update);
    setTimeout(update, 200);
  };
  if (document.readyState !== 'loading') setupScrollHint();
  else document.addEventListener('DOMContentLoaded', setupScrollHint);

  // ---------- Lifestyle carousel ----------
  // Hitch × High Rise campaign photos. Three tiles on desktop, one on
  // mobile (handled in CSS). Each tile auto-rotates through a private
  // shuffled queue so adjacent tiles never collide and the section
  // looks alive without being noisy.
  const LIFESTYLE_PHOTOS = [
    './img/lifestyle/film-01.jpg',
    './img/lifestyle/film-02.jpg',
    './img/lifestyle/film-03.jpg',
    './img/lifestyle/film-04.jpg',
    './img/lifestyle/film-06.jpg',
    './img/lifestyle/film-08.jpg',
    './img/lifestyle/final-001.jpg',
    './img/lifestyle/final-008.jpg',
    './img/lifestyle/final-020.jpg',
    './img/lifestyle/final-030.jpg',
    './img/lifestyle/final-040.jpg',
    './img/lifestyle/final-050.jpg',
    './img/lifestyle/final-056.jpg',
    './img/lifestyle/final-068.jpg',
    './img/lifestyle/final-078.jpg',
  ];

  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const buildLifestyle = () => {
    const grid = document.getElementById('lifestyle-grid');
    if (!grid) return;
    const TILE_COUNT = 3;
    const pool = shuffle(LIFESTYLE_PHOTOS);
    // Split the pool into TILE_COUNT independent queues so no tile
    // shows the same photo at the same time as another tile.
    const queues = Array.from({ length: TILE_COUNT }, () => []);
    pool.forEach((p, i) => queues[i % TILE_COUNT].push(p));

    grid.innerHTML = '';
    queues.forEach((queue, idx) => {
      const tile = document.createElement('figure');
      tile.className = 'ls-tile';
      // Two stacked imgs so we can cross-fade between current and next
      const a = document.createElement('img');
      const b = document.createElement('img');
      a.loading = idx === 0 ? 'eager' : 'lazy';
      b.loading = 'lazy';
      a.decoding = 'async';
      b.decoding = 'async';
      a.alt = '';
      b.alt = '';
      a.src = queue[0];
      a.classList.add('is-visible');
      tile.appendChild(a);
      tile.appendChild(b);
      grid.appendChild(tile);

      // Rotation state per tile
      let cur = 0;
      let active = a, hidden = b;
      const advance = () => {
        cur = (cur + 1) % queue.length;
        hidden.src = queue[cur];
        // Wait for the new image to decode before swapping visibility,
        // so we never crossfade to a half-loaded photo.
        const swap = () => {
          hidden.classList.add('is-visible');
          active.classList.remove('is-visible');
          [active, hidden] = [hidden, active];
        };
        if (hidden.decode) {
          hidden.decode().then(swap).catch(swap);
        } else {
          setTimeout(swap, 50);
        }
      };
      // Stagger each tile's rotation so they don't all swap together
      setTimeout(() => {
        setInterval(advance, 4500);
      }, idx * 1500);
    });
  };

  if (document.readyState !== 'loading') {
    buildLifestyle();
  } else {
    document.addEventListener('DOMContentLoaded', buildLifestyle);
  }

  // ---------- Smooth scroll ----------
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const id = a.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  if (yearEl) yearEl.textContent = new Date().getFullYear();

  // ---------- Init ----------
  // Deep-link support — catalog landing routes click straight here:
  //   ?sku=<key>     → pre-select a SKU tile (e.g. pack-bigthree)
  //   ?format=<key>  → pre-select a format tab (e.g. 4pack)
  //   ?flavor=<key>  → after the SKU is set, pick this flavor inside it
  //   (combinable: ?format=4pack&flavor=can-blueberry lands on 4-Pack
  //    tab + Blueberry active)
  const urlParams = new URLSearchParams(location.search);
  const skuParam    = urlParams.get('sku');
  const formatParam = urlParams.get('format');
  const flavorParam = urlParams.get('flavor');

  renderFormatTabs();
  renderStrengthChips();
  renderSkuScroller();

  if (skuParam && SKUS[skuParam]) {
    selectSku(skuParam);
  } else if (formatParam && FORMATS.some((f) => f.key === formatParam)) {
    selectFormat(formatParam);
  } else {
    selectSku('pack-horizon');  // default: top-seller
  }

  // Apply flavor AFTER SKU/format so the flavor picker has the right
  // available-flavor list to pick from. Falls through silently if the
  // flavor isn't valid for the current SKU.
  if (flavorParam && PRODUCTS[flavorParam]) {
    const sku = SKUS[currentSku];
    const available = sku?.kind === 'flavored'
      ? sku.flavors.filter((h) => PRODUCTS[h]?.strengths?.[sku.strength])
      : [];
    if (available.includes(flavorParam)) selectFlavor(flavorParam);
  }
})();
